/**
 * The one place the app-quit close-guard's `recording_status` is released.
 *
 * THE RULE, and it is the whole file: a session's `recording_status` is
 * released exactly when THAT SESSION'S RECOVERY has reached a state from which
 * no further recovery attempt will be made — it succeeded, or it failed in a
 * way nothing will retry. It is never released because a recording epoch
 * ended. A pause ends an epoch and the consultation is still live; a device
 * auto-switch ends an epoch and the consultation is still live. Neither
 * releases.
 *
 * The asymmetry is why every classifier below fails towards NOT releasing.
 * Leaving the guard armed too long warns a clinician about a consultation that
 * already finished — an annoyance. Releasing it early leaves it off at the
 * moment it is protecting a live consultation, and that loses audio.
 *
 * THE GATE LIVES WITH THE WRITE. `releaseCloseGuardAfterRecovery` is the only
 * function in the app that PATCHes `recording_status` to IDLE for the guard,
 * and it runs the whole per-session, per-epoch liveness gate itself, at the
 * moment of the write. That is structural, not tidiness. A check is about the
 * MOMENT IT RUNS: a caller that decided "this session's recording is over" at
 * stop-intent and hands that answer to an upload settling minutes later has
 * stated an INTENTION about the past, not a fact about the present — in
 * between, the clinician can start a second recording on the same session and
 * `recording_status` goes back to RECORDING. So a caller's statement is an
 * INPUT to the gate and never a substitute for it, and a new call site inherits
 * the gate by construction rather than by its author remembering.
 *
 * NO CHANGEABLE SIGNAL CROSSES AN AWAIT. Two await sites sit on the
 * release path — the native liveness IPC and the PATCH itself. A value read
 * before either of them describes a moment that has passed by the time the next
 * line runs. A PAUSE writes `PAUSED` into the session cache and a START writes
 * `RECORDING` there, both optimistically and both invisible to every other
 * signal here, so the cache is re-read after each await before the release
 * reports success. After the PATCH that re-read is compared against a snapshot
 * taken with NO await between it and the write, because a cached `RECORDING`
 * only means a start when it was not already there. Signals 1 and 2 are exempt
 * and do not need re-reading: both describe an epoch that has already ended,
 * and neither a caller's stated intent nor a recorded terminal outcome for that
 * recording can change after it.
 *
 * THE COUNT IS TWO BECAUSE NOTHING IS AWAITED AFTER THE POST-PATCH READ. A
 * re-read can only cover a window that CLOSES before it; a signal arriving
 * after the last read is covered by nothing, so the success path ends at the
 * cache write. The deliberate absence of an invalidation is that rule, not an
 * oversight: an invalidation would be a third await with no read after it, its
 * refetch resolves from a server that desktop never tells about a RECORDING, and
 * what lands is the IDLE this call just wrote — over a start or a pause that
 * arrived in the meantime, in the one cache `findActiveSessionFromCache` reads.
 * The PATCH's own deadline belongs to the same rule: an unbounded await is one
 * no later line ever gets to cover.
 *
 * This governs a GUARD. It says nothing about when capture may be stopped, and
 * the capture rule in `specs/012-app-8723-self-healing-stop/` is not to be read
 * as this one. Different objects.
 */

import { updateSessionById } from '@/pages/api/scribeAPI';
import { recorderFmc } from '@/tauri/controller/recording';
import { isTauri } from '@/tauri/utils';
import type {
  BackupRecoveryRow,
  StopReason,
  TerminalClass,
} from '@/types/self-healing-stop';
import { bridgeErrorKind } from '@/utils/logger/bridge-error-kind';
import { logger } from '@/utils/logger/logger';
import { sanitizeError } from '@heidi/runtime/logger';
import { RecordingStatus } from '@heidi/schema/ml-scribe';
import { GET_SESSION_BY_ID, type QuerySessionResponse } from '@heidi/session/contracts';
import { signalWithTimeout } from '@heidi/session/data';
import type { QueryClient } from '@tanstack/react-query';
import { peekTerminalOutcomeForRecording } from './terminalOutcomeRelay';

/**
 * Request deadline for the corrective `IDLE` PATCH below.
 *
 * Same name and same number as `useMutateUpdateSessionRecordingStatus`, which
 * this path's raw `updateSessionById` call replaced, and for the same reason:
 * the shared Axios instance is created with no `timeout`, so an unbounded write
 * leaves a stalled socket pending forever. That matters more here than there —
 * `useMutateUploadBackupRecording`'s `onSuccess` AWAITS this helper, so a hung
 * PATCH hangs the mutation with it, and the log that would say so is in a catch
 * this call would never reach.
 *
 * Duplicated rather than imported because it is module-private there. Two
 * writers of the same field, bounded the same way, is the point; if a third
 * appears the constant should move to a shared home rather than be copied again.
 */
const RECORDING_STATUS_REQUEST_TIMEOUT_MS = 30_000;

/**
 * How far a recovery has got. Two values on purpose: the rule is a yes/no over
 * "will anything else be attempted", and a third value would invite a caller to
 * invent a middle that releases.
 */
export type RecoveryProgress =
  /** Nothing further will be attempted — released. */
  | 'finished'
  /** Running, or a failure something may attempt again — NOT released. */
  | 'openOrRetryable';

/**
 * Where a release came from. Logged, never branched on: it is what makes a
 * "released nothing" line in production diagnosable without a second deploy.
 */
export type ReleaseOrigin =
  | 'backupUploadSucceeded'
  | 'backupUploadWillNotRetry'
  | 'recoveryRow'
  | 'nativeStop';

/**
 * What a caller has to say about whether the consultation is over.
 *
 * Required, and three-valued rather than an optional boolean, so that "I have
 * no view" is said out loud instead of arriving as an absent field that reads
 * like `false`. It is an INPUT to the gate below, never the gate: a caller
 * computes it when it asks for the release and the write happens later, so at
 * best it is a statement about the moment the caller spoke.
 */
export type ConsultationIntent =
  /**
   * The caller believes this was a FINAL stop — the consultation is over. The
   * gate still runs; this only means the caller is not itself refusing.
   */
  | 'ended'
  /**
   * The caller knows the consultation carries on — a pause, a device
   * auto-switch, or any path whose own contract is default-off. Refuses
   * outright, without spending an IPC on the liveness read.
   */
  | 'continues'
  /**
   * The caller has no view; the gate decides on its own evidence. What the
   * native-owned paths pass — they observe a recovery finishing and have no
   * standing to claim the consultation ended.
   */
  | 'unstated';

/**
 * Release a session's `recording_status`, moving SERVER truth — but only after
 * asking, AT THE MOMENT OF THE WRITE, whether that session is live.
 *
 * ## The gate, and why it is here rather than at a call site
 *
 * Four signals answer "is THIS session's consultation still live", in cost
 * order, and any one of them saying live refuses. They are asked here, inside
 * the one function that writes, so that every release — front-end, native,
 * present and future — asks the same question at the same instant as its write:
 *
 *  1. **The caller's intent.** Free, and the only signal that knows a pause or
 *     a device auto-switch from a final stop on a path that holds no native
 *     recording id. It is a claim about when the caller spoke, so it may refuse
 *     but may never, on its own, permit.
 *  2. **How THIS epoch ended.** From the caller when it holds the reason, else
 *     from the terminal-outcome relay keyed by this recording id. A `pause` /
 *     `deviceChange` ending means the consultation carried on. This is the only
 *     signal that sees the window where nothing is capturing and yet the
 *     consultation is live. Not knowing is not an ending that releases — it is
 *     this test declining to answer.
 *  3. **The session cache** ({@link cachedSessionRecordingStatus}). Free and
 *     synchronous, so it is read before and after awaits rather than carried.
 *     `PAUSED` refuses wherever it is read. `RECORDING` refuses in exactly one
 *     place — after the PATCH, and only when the snapshot taken with no await
 *     before the PATCH did not already hold it, which is what tells a start
 *     that landed during the write from the stale value this path exists to
 *     clear. A pause or a start in either window is visible to nothing else.
 *  4. **The native recorder, attributed to a session.** Capturing FOR THIS
 *     SESSION refuses; capturing for a different one does not. This is the
 *     signal that catches the case the cache cannot distinguish: a second
 *     recording started on the same session after the caller formed its intent
 *     and before this upload settled.
 *
 * An unreadable or absent liveness snapshot does not release. "Cannot tell" is
 * treated as "may still be recording", the direction every classification on
 * this path fails in.
 *
 * ## Why the server is NOT a liveness signal
 *
 * Desktop never PATCHes `recording_status: RECORDING`: the status mutation
 * writes RECORDING optimistically into the cache and returns before the network
 * call. After a pause followed by a resume, the server can still hold the old
 * `PAUSED` value while the native recorder has moved on. Treating that server
 * value as a refusal traps the guard in the crash/reload case this release path
 * exists to clear. The server is only the target of the corrective IDLE write;
 * liveness comes from the caller/epoch/cache/native signals above.
 *
 * ## The one signal that cannot apply on every path, and why
 *
 * Signal 2's relay lookup needs the NATIVE recording id, and the front-end
 * backup-upload path does not have one — its variables carry an FE-minted
 * `audioId` and a file path, and the relay is keyed by the native
 * `(recordingId, generation)` pair a durable recovery row produces. So that
 * path passes no `recordingId` and signal 2 declines to answer, exactly as it
 * does for an epoch whose outcome was evicted. Its per-epoch question is
 * answered instead by signal 1, computed at the stop from the same `isPaused`
 * that decides `pause` from a final stop, and by signals 3 and 4, which run
 * unchanged at the write. Signals 3 and 4 are what make the front-end path safe
 * against a second recording having started since.
 *
 * ## The two ways the write itself can fail to land
 *
 *  - `updateSessionById` resolves `{ data: null }` for a falsy session id. It
 *    is safe, but it reports a success while releasing nothing — so the id is
 *    checked here and a missing one logs and returns without calling.
 *  - The session-status mutation hook writes the cache optimistically and its
 *    `onError` only logs. A rejected PATCH would leave the cache reading IDLE
 *    while the server still held RECORDING, and the next refetch would re-arm
 *    the very guard the release was meant to close. So the server update goes
 *    first and the cache is written only after it succeeds.
 *
 * The same optimistic write is why the cache update after a SUCCESSFUL PATCH is
 * conditional, and it is conditional on TWO values rather than one. A pause
 * landing during the PATCH puts PAUSED in the cache through that hook's
 * `onMutate`, and a second recording STARTING during it puts RECORDING there by
 * the same route. Writing IDLE over either takes the guard down on a
 * consultation that is paused or capturing right now, and for the start it is
 * worse than a disagreeing cache: desktop never PATCHes RECORDING, so any
 * refetch of this query reads back the IDLE this call just wrote and the
 * started recording is gone from the cache with it, which is the only place
 * `findActiveSessionFromCache` looks. Neither happens: the entry stands and the
 * call reports `false` — the server did move, but the guard this function
 * answers about is armed again and the other write is what the session stands
 * on. That refetch is also why the successful path issues none of its own.
 *
 * A cached RECORDING only means a start when it was not there BEFORE the write.
 * The status read with no await between it and the PATCH is kept as the
 * snapshot for exactly that comparison: a RECORDING already present is the
 * stale value a crash left behind — the one this whole path exists to clear,
 * with every liveness signal having already permitted — and is still
 * overwritten. Getting that branch wrong costs the feature rather than a
 * consultation.
 *
 * Refusing to correct the cache leaves it possibly disagreeing with the server,
 * which is the armed-too-long direction every classification here fails in.
 *
 * Safe to call twice for the same finished recovery: the second call issues the
 * same `recording_status: IDLE` PATCH, and a PATCH that sets a field to the
 * value it already holds is a no-op in the only sense this path depends on —
 * the value afterwards is IDLE either way. That is a statement about what this
 * client needs, not a verified claim about the endpoint's internals; nothing
 * front-end-side can observe the difference, so no caller may build on more
 * than it.
 *
 * @returns whether the guard is DOWN as this call leaves it — the server moved
 * to IDLE and the cache agrees — not whether the PATCH landed. The two differ
 * in exactly two cases, both a signal arriving during the PATCH: a pause, and a
 * recording starting where none was cached before it. In both the write
 * succeeded and the answer is still `false`, because the session is live again
 * and the guard is armed on purpose. `true` describes the cache AT THE MOMENT
 * THIS CALL RETURNED and nothing later: no refetch is scheduled, so a start
 * arriving after it simply stands. No production caller branches on it
 * (both call sites await or void the promise); it is read by the tests, and a
 * future caller that wants "did the PATCH go out" must not read this.
 */
export async function releaseCloseGuardAfterRecovery({
  sessionId,
  recordingId,
  epochEndReason,
  consultationIntent,
  queryClient,
  origin,
  isCancelled,
}: {
  sessionId: string | null | undefined;
  /**
   * The NATIVE recording id whose epoch this was, when the caller has one. Its
   * only use is the relay lookup in signal 2; a caller without one passes
   * nothing and that signal declines to answer.
   */
  recordingId?: string | null;
  /**
   * How this epoch ended, when the caller already holds it. `undefined`/`null`
   * means ask the relay — never "it ended cleanly".
   */
  epochEndReason?: StopReason | null;
  consultationIntent: ConsultationIntent;
  queryClient: QueryClient;
  origin: ReleaseOrigin;
  /**
   * Checked after the awaited liveness read and again with no await between it
   * and the PATCH, so a listener torn down mid-gate (a flag flip, a sign-out,
   * an unmount) does not land a write its effect no longer owns. NOT checked
   * after the PATCH: the write has already landed there, and leaving the cache
   * disagreeing with the server it just moved buys nothing.
   */
  isCancelled?: () => boolean;
}): Promise<boolean> {
  if (!sessionId) {
    logger.warn('[close-guard] release skipped: no session id to release', {
      origin,
    });
    return false;
  }

  // The close guard is desktop-only: a web build has no app to quit and no
  // guard to release, and the liveness read below has no recorder to ask. Web
  // therefore writes nothing at all rather than writing unguarded.
  if (!isTauri()) return false;

  if (consultationIntent === 'continues') {
    logger.info(
      '[close-guard] the caller says this consultation continues; not releasing',
      {
        origin,
      }
    );
    return false;
  }

  const reason =
    epochEndReason ??
    (recordingId ? peekTerminalOutcomeForRecording(recordingId)?.reason : undefined) ??
    null;
  if (reason && epochEndLeavesConsultationLive(reason)) {
    logger.info(
      '[close-guard] this epoch ended while the consultation continues; not releasing',
      { origin, reason }
    );
    return false;
  }

  // Signal 3, read here only to refuse an ALREADY-paused session before the
  // IPC below is spent. It is not the read the decision is made on; that one is
  // taken after the await, because a pause can land during it.
  if (cachedSessionRecordingStatus(queryClient, sessionId) === RecordingStatus.PAUSED) {
    logger.info('[close-guard] this session is paused; not releasing', {
      origin,
    });
    return false;
  }

  if ((await nativeLivenessForSession(sessionId, origin)) !== 'notCapturingHere') {
    return false;
  }
  if (isCancelled?.()) return false;

  // Signal 3 again. A pause landing during the awaited liveness read writes
  // PAUSED into the cache (`useMutateUpdateSessionRecordingStatus.onMutate`)
  // and no other signal can see it, so the value is re-read rather than carried
  // across the await.
  const cachedStatus = cachedSessionRecordingStatus(queryClient, sessionId);
  if (cachedStatus === RecordingStatus.PAUSED) {
    logger.info('[close-guard] this session is paused; not releasing', {
      origin,
    });
    return false;
  }

  // An absent cache entry is not a reason to ask the server. Desktop never
  // PATCHes RECORDING, so after pause→resume the server can still say PAUSED;
  // using that stale value as liveness traps the guard after a crash.

  // The last reads before the write, and no await separates them from the
  // PATCH. Everything above describes a moment at least one round trip old;
  // these two are what the decision to write IDLE actually rests on.
  //
  // The status read here is ALSO the snapshot the post-PATCH check compares
  // against, which is why its value is kept rather than read a third time:
  // taken any earlier it would itself be a changeable signal that crossed an
  // await, and the comparison would be deciding on a value the PATCH had
  // already outrun. Nothing may be awaited between this line and the write.
  if (isCancelled?.()) return false;
  const statusBeforeWrite = cachedSessionRecordingStatus(queryClient, sessionId);
  if (statusBeforeWrite === RecordingStatus.PAUSED) {
    logger.info('[close-guard] this session paused before the write; not releasing', {
      origin,
    });
    return false;
  }

  try {
    await updateSessionById(
      sessionId,
      { recording_status: RecordingStatus.IDLE },
      signalWithTimeout(undefined, RECORDING_STATUS_REQUEST_TIMEOUT_MS)
    );
  } catch (error) {
    // An AxiosError serialises `config` (URL, Authorization header, body) and
    // `response.data`; only the status and code may leave the client.
    //
    // `errorKind` beside it because the deadline's own rejection is NOT an
    // AxiosError with a status: axios's abort listener rejects with a
    // `CanceledError`, which `sanitizeError` projects to `{}`. Without the
    // classification this line could not tell an expired deadline from a
    // network failure — and the classification reads `name`/`errorCode`, never
    // the message, which is where a URL or a session id would be.
    logger.warn('[close-guard] release failed to reach the server', {
      origin,
      errorKind: bridgeErrorKind(error),
      error: sanitizeError(error),
    });
    return false;
  }

  // The PATCH is itself an await, so the cache is read once more before it is
  // touched. Two things can have landed in it during the write, both through
  // the status mutation's `onMutate` and neither visible to any other signal
  // here: a PAUSE, and a second recording STARTING. Overwriting either with
  // IDLE takes the guard down on a consultation that is paused or capturing
  // right now.
  //
  // The start is told from the stale RECORDING this path exists to clear by the
  // snapshot above, and only by it: a RECORDING that was ALREADY cached before
  // the write is what a crash left behind, every liveness signal has permitted,
  // and it is still overwritten. Only a RECORDING that APPEARED across the
  // write is a start.
  //
  // Both refusals skip the cache write and report no release: the server did
  // move, but the other write is what this session stands on.
  const statusAfterWrite = cachedSessionRecordingStatus(queryClient, sessionId);
  if (statusAfterWrite === RecordingStatus.PAUSED) {
    logger.info('[close-guard] this session paused during the write; leaving it paused', {
      origin,
    });
    return false;
  }
  if (
    statusAfterWrite === RecordingStatus.RECORDING &&
    statusBeforeWrite !== RecordingStatus.RECORDING
  ) {
    logger.info(
      '[close-guard] a recording started during the write; leaving it recording',
      { origin }
    );
    return false;
  }

  // THE LAST STATEMENT ON THE SUCCESS PATH, and nothing may be awaited after it.
  //
  // There is deliberately no invalidation here. An invalidation is a third
  // await with no read after it, so the window it opens is covered by nothing:
  // the refetch resolves from the server, desktop never PATCHes RECORDING, and
  // what lands is therefore the IDLE this call just wrote — over a RECORDING or
  // PAUSED that a start or a pause put in the cache optimistically in the
  // meantime. `findActiveSessionFromCache` sees only cached RECORDING/PAUSED,
  // so that refetch takes the guard down on a live consultation, one await
  // later than the post-PATCH check above closes. Nothing is lost by dropping
  // it: this write is what the guard reads, and the server already holds the
  // value the refetch would fetch.
  queryClient.setQueryData<QuerySessionResponse>(
    [GET_SESSION_BY_ID, { sessionId }],
    (old) => (old ? { ...old, recording_status: RecordingStatus.IDLE } : old)
  );
  return true;
}

/**
 * Signal 4's answer, or that it cannot be attributed to a session —
 * `unreadable` covers both the recorder that could not be asked and the
 * capturing one whose answer names no session at all.
 */
type NativeLivenessForSession = 'capturingHere' | 'notCapturingHere' | 'unreadable';

/**
 * Ask the native recorder whether IT IS CAPTURING FOR THIS SESSION — signal 4.
 *
 * A function rather than an inline block because the same classification is
 * load-bearing anywhere this helper is used; a second inline copy is how one of
 * them drifts.
 *
 * Only `notCapturingHere` permits. Both other answers refuse, including
 * `unreadable`: an absent or rejected liveness snapshot — or a capturing one
 * that names no session — is "cannot tell", and cannot-tell is treated as
 * may-still-be-recording here as everywhere else.
 */
async function nativeLivenessForSession(
  sessionId: string,
  origin: ReleaseOrigin
): Promise<NativeLivenessForSession> {
  let liveness;
  try {
    liveness = await recorderFmc.getRecordingLivenessQuietly();
  } catch (error) {
    // A classification, never the caught error: an IPC rejection can embed a
    // backup file path, and the Datadog transport redacts nothing. `errorKind`
    // is what makes that true here — this command rejects with a
    // `SelfHealingBridgeError`, never an axios one, so `sanitizeError` alone
    // projects to `{}` and this line would say only that something failed.
    logger.warn('[close-guard] native liveness unreadable; not releasing', {
      origin,
      errorKind: bridgeErrorKind(error),
      error: sanitizeError(error),
    });
    return 'unreadable';
  }
  if (!liveness) {
    logger.warn('[close-guard] no native liveness to read; not releasing', {
      origin,
    });
    return 'unreadable';
  }

  // `session_id` is only trustworthy while the recorder is actually on: a
  // stopped one keeps the last id it held, and reading that would refuse every
  // release for the session that just finished.
  //
  // Written as "not Stopped" rather than by naming the capturing states, so it
  // fails in the same direction as every classifier in this file. `State` is
  // ts-rs-generated from Rust: a variant added there — `Stopping`, `Starting` —
  // widens the union without breaking a `===` comparison, so an allow-list
  // would silently read the new variant as not capturing and release over a
  // recorder that is still on. Every other reader of this union in the app
  // spells it the same way (`recorder-liveness.ts`,
  // `recoverDesktopRecordingAfterReload.ts`, `isDesktopRecordingActive.ts`).
  const capturing = liveness.state !== 'Stopped';
  if (capturing) {
    // A capturing recorder that names NO session is CANNOT-TELL, not "some
    // other session". `session_id` is typed non-nullable by ts-rs and an empty
    // string is what an unattributed snapshot carries, so `=== sessionId` reads
    // it as a different session and permits — the one spelling that fails the
    // opposite way to the rest of this file. Nothing here can rule out that the
    // capture belongs to `sessionId`, so it refuses like every other
    // cannot-tell. `recoverDesktopRecordingAfterReload` already classifies this
    // exact snapshot as `missing_session_id`; same name, so the two paths read
    // as one thing in Datadog.
    if (!liveness.session_id) {
      logger.warn(
        '[close-guard] the recorder is capturing but names no session; not releasing',
        { origin, reason: 'missing_session_id', state: liveness.state }
      );
      return 'unreadable';
    }
    if (liveness.session_id === sessionId) {
      logger.info('[close-guard] this session is still recording; not releasing', {
        state: liveness.state,
        origin,
      });
      return 'capturingHere';
    }
  }

  return 'notCapturingHere';
}

/**
 * What a durable recovery row says about its session's recovery.
 *
 * A `switch` with a `never` fallthrough, not an or-chain: TypeScript cannot
 * report a non-exhaustive `===` comparison, so a status added to the row would
 * be silently dropped. It now fails to compile until someone decides, in
 * writing, which side of the release rule it falls on.
 */
export function recoveryRowProgress(
  status: BackupRecoveryRow['status']
): RecoveryProgress {
  switch (status) {
    // The native side owns the file and is working on it: a failed attempt is
    // retried in process and the next launch re-attempts the rest.
    case 'pending':
    case 'uploading':
      return 'openOrRetryable';
    // Retries are exhausted or the max age expired, but the audio is KEPT and
    // the clinician is offered a native retry for it. A further attempt can
    // still be made, so this does not release — the guard stays armed for as
    // long as that retry is on the table.
    case 'abandonedRetained':
      return 'openOrRetryable';
    // The backup landed, or the server said the target is gone and the retained
    // copy was securely deleted. Either way nothing is pending and nothing will
    // be attempted again.
    case 'uploaded':
    case 'terminalEvicted':
      return 'finished';
    default: {
      const exhaustive: never = status;
      return exhaustive;
    }
  }
}

/**
 * Whether an epoch ENDING for this reason leaves the consultation live.
 *
 * The per-EPOCH half of the gate, and the question `recoveryRowProgress` cannot
 * answer: a durable row says the backup for one recording finished, never why
 * that recording stopped. Two reasons end an epoch and carry the consultation
 * straight on into the next one, and in both the app-quit guard must stay
 * armed:
 *
 *  - `pause` — the self-healing pause is a full segment stop (finalize, drain,
 *    retain), so the native recorder reports nothing capturing for as long as
 *    the clinician is paused, and the paused epoch's backup settles `uploaded`
 *    seconds later. Releasing on it would PATCH IDLE over a PAUSED consultation
 *    and take the quit warning down for the rest of the pause.
 *  - `deviceChange` — the same shape with a narrower window: between the old
 *    epoch stopping and the new one going active, nothing is capturing.
 *
 * Every other reason ends the epoch without, by itself, keeping the
 * consultation going, so none of them refuses here. That includes
 * `startConvergence`, which force-stops a stale epoch so a start can proceed:
 * whether a consultation continues after it is decided by WHOSE recording
 * starts next, which is a per-session question the caller's liveness check
 * answers directly — guessing it here would trap a converged-away session's
 * guard forever.
 *
 * Exhaustive by a `never` fallthrough, like every other classifier in this
 * file: a new reason has to be placed on one side in writing.
 */
export function epochEndLeavesConsultationLive(reason: StopReason): boolean {
  switch (reason) {
    case 'pause':
    case 'deviceChange':
      return true;
    case 'userStop':
    case 'feFallback':
    case 'startConvergence':
    case 'shutdown':
    case 'watchdogOrphanedNoLease':
    case 'watchdogNoProgress':
    case 'watchdogMaxDuration':
    case 'watchdogStopDeadline':
    case 'watchdogTerminalResponses':
    case 'watchdogChunkCeiling':
      return false;
    default: {
      const exhaustive: never = reason;
      return exhaustive;
    }
  }
}

/**
 * The cached `recording_status` for a session — signal 3 — or `undefined` when
 * the cache has no value for it.
 *
 * THREE-VALUED, AND NOT A PREDICATE. `PAUSED` refuses, another value permits,
 * and NO VALUE is a third thing: the cache declining to answer. There is
 * deliberately no `cachedSessionIsPaused` boolean here for a later caller to
 * collapse absence into evidence of a non-pause.
 *
 * The values are read asymmetrically by the gate, and that is not an oversight:
 *
 *  - `PAUSED` refuses. Only a deliberate pause writes it, and the pause path
 *    writes it at the moment it pauses, so there is no ambiguity to weigh. The
 *    cost is named rather than hidden — a consultation that died while paused
 *    keeps its guard armed for as long as that cached `PAUSED` is on hand,
 *    which is the annoyance direction of this feature's asymmetry.
 *  - `RECORDING` does NOT refuse. A stale `RECORDING` is the exact value this
 *    whole path exists to clear — a consultation whose app died mid-recording
 *    leaves it behind and nothing else ever corrects it — so reading it as
 *    "live" would mean the guard could never be released for the one case it
 *    was written for. Whether a session is REALLY recording right now is
 *    signal 4's question, answered by the native recorder, which cannot go
 *    stale across a webview reload. The one exception is a `RECORDING` that
 *    APPEARED across the PATCH, which the gate tells from a stale one by
 *    comparing this reader's value against a snapshot taken with no await
 *    before the write. That comparison belongs to the gate: every call here
 *    still returns the value and reads nothing into it.
 *
 * A cache entry that carries no `recording_status` at all reads the same as no
 * entry, deliberately: in both, there is no value here to answer with.
 */
function cachedSessionRecordingStatus(
  queryClient: QueryClient,
  sessionId: string
): RecordingStatus | undefined {
  const cached = queryClient.getQueryData<QuerySessionResponse>([
    GET_SESSION_BY_ID,
    { sessionId },
  ]);
  return cached?.recording_status ?? undefined;
}

/**
 * Whether a native stop is one the front end never initiated, and is therefore
 * this listener's to release.
 *
 * Exhaustive for the same reason as above, and the reasons split three ways:
 *
 *  - `pause` and `deviceChange` end a recording epoch while the consultation is
 *    still live. The release rule names both explicitly: they never release.
 *  - `userStop`, `feFallback` and `startConvergence` are stops the front end
 *    itself asked for, so a front-end-owned path already owns their release.
 *    Name it, because the obvious candidate is the wrong one: it is the stop's
 *    own `recording_status: IDLE` write (`useRecordingButton`,
 *    `usePendingRecordingAction`, `SessionRecordingOverlay`, mini-mode), NOT
 *    the recovery releaser in `useMutateUploadBackupRecording`. That
 *    distinction is load-bearing for the healthy stop whose backup is
 *    `backupPendingAsync` or already `backupUploaded`: it dispatches no FE
 *    upload, so the recovery releaser never runs for it — and the reason this
 *    branch is still safe is the stop's own IDLE write, which is dispatched
 *    from the click and does not depend on a backup existing at all. Releasing
 *    here would add a second IDLE write AT THAT WRITE'S OWN INSTANT, which is
 *    the one moment it buys nothing: this listener fires as `terminal::execute`
 *    reports the ending, while the click's PATCH is still in flight.
 *
 *    Read narrowly: it is a statement about THIS call site, not a rule that
 *    only one releaser may ever exist for such a stop. The row-driven paths do
 *    not classify by reason at all, so a finished recovery row for a `userStop`
 *    releases through `recoveryRow` / `backupUploadSucceeded` regardless. That
 *    is intended and safe — the helper is specified as safe to call twice for
 *    the same finished recovery, and a row settles a whole backup lifetime
 *    after the click, not inside its round trip.
 *  - `shutdown` and every watchdog reason are stops the front end never
 *    initiated. Nothing front-end-side writes `recording_status` for them
 *    today, which is exactly the hole this closes.
 */
export function stopReasonReleasesHere(reason: StopReason): boolean {
  switch (reason) {
    case 'pause':
    case 'deviceChange':
      return false;
    case 'userStop':
    case 'feFallback':
    case 'startConvergence':
      return false;
    case 'shutdown':
    case 'watchdogOrphanedNoLease':
    case 'watchdogNoProgress':
    case 'watchdogMaxDuration':
    case 'watchdogStopDeadline':
    case 'watchdogTerminalResponses':
    case 'watchdogChunkCeiling':
      return true;
    default: {
      // The `never` is the compile-time gate and stays. What it cannot do is
      // decide the RUNTIME case, where native is ahead of this union and
      // `reason` is a real non-empty string: returning it would be truthy and
      // would PERMIT, the one default in this file that fails towards
      // releasing. The other three defaults return a value their callers
      // compare against `'finished'`/read as `true`, so an unknown one already
      // refuses; this one has to say so.
      const exhaustive: never = reason;
      void exhaustive;
      return false;
    }
  }
}

/**
 * What a native stop's own classification says about the recovery.
 *
 * Only a clean, server-finalized stop is treated as finished here. The other
 * two are deliberately read as still open:
 *
 *  - `retained` means recoverable audio was kept on disk, so a durable recovery
 *    row owns what happens to it next.
 *  - `failed` covers a non-finalized stop and contamination as well as a
 *    server-reaped one, and this side cannot tell those apart from the
 *    classification alone. The rule says an undeterminable case is treated as
 *    retryable, so it is — and the row is what releases it once it settles.
 */
export function stopClassificationProgress(
  classification: TerminalClass
): RecoveryProgress {
  switch (classification) {
    case 'finalized':
      return 'finished';
    case 'retained':
    case 'failed':
      return 'openOrRetryable';
    default: {
      const exhaustive: never = classification;
      return exhaustive;
    }
  }
}
